import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaymentCategory } from "@/hooks/payments/usePaymentCategories";
import { Users } from "lucide-react";

interface CategoryFormProps {
  category?: PaymentCategory;
  onSubmit: (data: { name: string; amount: number; validity_days: number; covers_people: number }) => void;
  onCancel: () => void;
  isLoading?: boolean;
  /** Show the "Cantidad de personas cubiertas" field (Vikingos-only feature). */
  enableGroupCategories?: boolean;
}

export const CategoryForm = ({
  category,
  onSubmit,
  onCancel,
  isLoading,
  enableGroupCategories = false,
}: CategoryFormProps) => {
  const [name, setName] = useState(category?.name || "");
  const [amount, setAmount] = useState(category?.amount?.toString() || "");
  const [validityDays, setValidityDays] = useState(
    category?.validity_days?.toString() || "30"
  );
  const [coversPeople, setCoversPeople] = useState(
    category?.covers_people?.toString() || "1"
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      amount: Number(amount),
      validity_days: Number(validityDays),
      covers_people: Math.max(1, Number(coversPeople) || 1),
    });
  };

  const isValid = name.trim() && Number(amount) > 0 && Number(validityDays) > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="category-name">Nombre</Label>
        <Input
          id="category-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Cuota mensual"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category-amount">Monto ($)</Label>
        <Input
          id="category-amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Ej: 48000"
          min="1"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category-validity">Días de validez</Label>
        <Input
          id="category-validity"
          type="number"
          value={validityDays}
          onChange={(e) => setValidityDays(e.target.value)}
          placeholder="Ej: 30"
          min="1"
          required
        />
      </div>

      {enableGroupCategories && (
        <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
          <Label htmlFor="category-covers" className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Personas cubiertas por este plan
          </Label>
          <Input
            id="category-covers"
            type="number"
            value={coversPeople}
            onChange={(e) => setCoversPeople(e.target.value)}
            placeholder="1"
            min="1"
            max="20"
          />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Si es mayor a 1 (ej. <strong>2x1</strong> = 2), al cobrar esta categoría
            podrás elegir socios adicionales que también quedarán al día con el mismo pago.
          </p>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={!isValid || isLoading}
        >
          {category ? "Guardar" : "Crear"}
        </Button>
      </div>
    </form>
  );
};
